import { useCallback, useEffect, useRef, useState } from 'react'
import {
  IconPlayerPlayFilled,
  IconPlayerPauseFilled,
  IconPlayerTrackNextFilled,
  IconPlayerTrackPrevFilled,
} from '@tabler/icons-react'
import { useAppStore } from '../store'
import { useComputeMean } from '../useComputeMean'

const PLAY_INTERVAL_MS = 500
/** Default mean window when the user first opens mean mode: ~1 month
 *  centred on the playhead, snapped to the collection's cadence. */
const DEFAULT_WINDOW_DAYS = 30
/** Above this many steps, computing a mean triggers an explicit confirm. */
const HEAVY_MEAN_STEPS = 500
/** Minimum grabbable handle width; keeps very narrow brushes usable. */
const HANDLE_HIT_WIDTH = 8

const btnStyle: React.CSSProperties = {
  background: 'var(--paper)',
  border: '1px solid var(--rule)',
  borderRadius: 3,
  cursor: 'pointer',
  color: 'var(--teal)',
  padding: 3,
  height: 26,
  width: 26,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
}

function stepIntervalLabel(stepsPerDay: number | undefined): string {
  if (!stepsPerDay || stepsPerDay <= 0) return ''
  if (stepsPerDay === 24) return '1 h'
  if (stepsPerDay === 1) return '1 d'
  if (stepsPerDay > 24) return `${Math.round((24 / stepsPerDay) * 60)} min`
  if (stepsPerDay === 2) return '12 h'
  if (stepsPerDay === 4) return '6 h'
  if (stepsPerDay === 8) return '3 h'
  return `${(24 / stepsPerDay).toFixed(1)} h`
}

const TimeSlider = () => {
  const datasetModule = useAppStore((s) => s.getDatasetModule())
  const datasetState = useAppStore((s) => s.datasetState) as { time?: number }
  const setActiveDatasetState = useAppStore((s) => s.setActiveDatasetState)
  const timeCount = useAppStore((s) => s.timeCount)
  const timeStepsPerDay = useAppStore((s) => s.timeStepsPerDay)
  const formatTimeIndex = useAppStore((s) => s.formatTimeIndex)
  const reverseTimeIndex = useAppStore((s) => s.reverseTimeIndex)


  const timeMeanMode = useAppStore((s) => s.timeMeanMode)
  const setTimeMeanMode = useAppStore((s) => s.setTimeMeanMode)
  const timeMeanStartDate = useAppStore((s) => s.timeMeanStartDate)
  const timeMeanEndDate = useAppStore((s) => s.timeMeanEndDate)
  const setTimeMeanStartDate = useAppStore((s) => s.setTimeMeanStartDate)
  const setTimeMeanEndDate = useAppStore((s) => s.setTimeMeanEndDate)
  const timeMeanLoading = useAppStore((s) => s.timeMeanLoading)
  const timeMeanResult = useAppStore((s) => s.timeMeanResult)
  const zarrLayer = useAppStore((s) => s.zarrLayer)
  const { compute, clear: clearMean, error: meanError } = useComputeMean()

  const hasTime = timeCount > 1 && typeof datasetState.time === 'number'
  const currentIndex =
    typeof datasetState.time === 'number' ? datasetState.time : 0
  const max = Math.max(0, timeCount - 1)

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const trackRef = useRef<HTMLDivElement>(null)
  const brushTrackRef = useRef<HTMLDivElement>(null)
  const sliderRef = useRef<HTMLDivElement>(null)
  const [editingDates, setEditingDates] = useState(false)

  // Seed a ~1-month window centred on the playhead the first time the user
  // opens mean mode without any window already set.
  useEffect(() => {
    if (!timeMeanMode || !hasTime || !formatTimeIndex) return
    if (timeMeanStartDate || timeMeanEndDate) return
    const halfWindow = Math.floor(
      ((timeStepsPerDay ?? 1) * DEFAULT_WINDOW_DAYS) / 2,
    )
    const lo = Math.max(0, currentIndex - halfWindow)
    const hi = Math.min(max, currentIndex + halfWindow)
    setTimeMeanStartDate(formatTimeIndex(lo))
    setTimeMeanEndDate(formatTimeIndex(hi))
  }, [timeMeanMode, hasTime, formatTimeIndex]) // eslint-disable-line

  // On dataset switch: if the current brush no longer lies inside the new
  // extent, drop it — otherwise carry it over so it survives the switch.
  useEffect(() => {
    if (!formatTimeIndex || !reverseTimeIndex) return
    if (!timeMeanStartDate || !timeMeanEndDate) return
    const lo = reverseTimeIndex(timeMeanStartDate)
    const hi = reverseTimeIndex(timeMeanEndDate)
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo < 0 || hi > max) {
      setTimeMeanStartDate(null)
      setTimeMeanEndDate(null)
    }
  }, [datasetModule]) // eslint-disable-line

  const brushStart =
    timeMeanStartDate && reverseTimeIndex
      ? Math.max(0, Math.min(max, Math.round(reverseTimeIndex(timeMeanStartDate))))
      : 0
  const brushEnd =
    timeMeanEndDate && reverseTimeIndex
      ? Math.max(0, Math.min(max, Math.round(reverseTimeIndex(timeMeanEndDate))))
      : max
  const windowSteps = Math.max(0, brushEnd - brushStart + 1)

  const stop = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    setIsPlaying(false)
  }

  const start = () => {
    if (timerRef.current || max === 0) return
    setIsPlaying(true)
    timerRef.current = setInterval(() => {
      const s = useAppStore.getState()
      const cur =
        typeof (s.datasetState as any).time === 'number'
          ? ((s.datasetState as any).time as number)
          : 0
      const next = cur + 1 > max ? 0 : cur + 1
      s.setActiveDatasetState({ time: next })
    }, PLAY_INTERVAL_MS)
  }

  useEffect(() => stop, [])
  useEffect(() => { stop() }, [datasetModule])

  const xToIndex = useCallback(
    (clientX: number, ref: React.RefObject<HTMLDivElement | null>): number => {
      const el = ref.current
      if (!el) return currentIndex
      const rect = el.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
      return Math.round(ratio * max)
    },
    [currentIndex, max],
  )

  // Playhead click-on-track: move the playhead to the pointer's index.
  const onPlayheadTrackPointerDown = (e: React.PointerEvent) => {
    // Guard: don't hijack the range input's own drag.
    if ((e.target as HTMLElement).tagName === 'INPUT') return
    stop()
    setActiveDatasetState({ time: xToIndex(e.clientX, trackRef) })
  }

  // Brush drag: three modes — start-handle, end-handle, mid (move window).
  const brushPointerDown = (e: React.PointerEvent) => {
    if (!formatTimeIndex || max === 0) return
    e.preventDefault()
    ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)

    const rect = brushTrackRef.current?.getBoundingClientRect()
    if (!rect) return
    const startXpx = rect.left + (brushStart / max) * rect.width
    const endXpx = rect.left + (brushEnd / max) * rect.width

    // Hit-test: which handle (with a ≥8px hit width)?
    const distStart = Math.abs(e.clientX - startXpx)
    const distEnd = Math.abs(e.clientX - endXpx)
    let mode: 'start' | 'end' | 'mid'
    if (distStart <= Math.max(HANDLE_HIT_WIDTH, distEnd)) mode = 'start'
    else if (distEnd <= HANDLE_HIT_WIDTH) mode = 'end'
    else mode = e.clientX > startXpx && e.clientX < endXpx ? 'mid' : 'end'

    // For 'mid' record where the pointer picked up the window so we translate.
    const grabIdx = xToIndex(e.clientX, brushTrackRef)
    const initialStart = brushStart
    const initialEnd = brushEnd

    const apply = (idx: number) => {
      if (!formatTimeIndex) return
      if (mode === 'start') {
        const lo = Math.max(0, Math.min(idx, initialEnd))
        setTimeMeanStartDate(formatTimeIndex(lo))
      } else if (mode === 'end') {
        const hi = Math.min(max, Math.max(idx, initialStart))
        setTimeMeanEndDate(formatTimeIndex(hi))
      } else {
        const delta = idx - grabIdx
        let lo = initialStart + delta
        let hi = initialEnd + delta
        if (lo < 0) { hi -= lo; lo = 0 }
        if (hi > max) { lo -= hi - max; hi = max }
        setTimeMeanStartDate(formatTimeIndex(lo))
        setTimeMeanEndDate(formatTimeIndex(hi))
      }
    }
    const onMove = (ev: PointerEvent) =>
      apply(xToIndex(ev.clientX, brushTrackRef))
    const onUp = (ev: PointerEvent) => {
      apply(xToIndex(ev.clientX, brushTrackRef))
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const requestCompute = () => {
    if (windowSteps > HEAVY_MEAN_STEPS) {
      const ok = window.confirm(
        `About to average ${windowSteps.toLocaleString()} timesteps — this may take a while. Continue?`,
      )
      if (!ok) return
    }
    compute()
  }

  if (!hasTime) return null

  const pctStart = max > 0 ? (brushStart / max) * 100 : 0
  const pctEnd = max > 0 ? (brushEnd / max) * 100 : 100
  const stepLabel = stepIntervalLabel(timeStepsPerDay)
  const label = formatTimeIndex ? formatTimeIndex(currentIndex) : String(currentIndex)
  const startLabel = formatTimeIndex ? formatTimeIndex(brushStart) : String(brushStart)
  const endLabel = formatTimeIndex ? formatTimeIndex(brushEnd) : String(brushEnd)
  const isMeanDisplayed = timeMeanResult !== null

  const playheadOpacity = isMeanDisplayed ? 0.45 : 1

  return (
    <div
      ref={sliderRef}
      style={{
        background: 'rgba(255,255,255,0.95)',
        border: '1px solid var(--rule)',
        borderRadius: 6,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: '8px 12px',
        fontSize: 12,
        fontFamily: 'var(--card-sans)',
        position: 'relative',
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* ---------- Lane 1: transport + playhead ---------- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: playheadOpacity }}>
        <button
          style={btnStyle}
          title="Step back"
          onClick={() => { stop(); setActiveDatasetState({ time: Math.max(0, currentIndex - 1) }) }}
        ><IconPlayerTrackPrevFilled size={13} /></button>
        <button
          style={{ ...btnStyle, background: isPlaying ? '#daeef8' : 'var(--paper)' }}
          title={isPlaying ? 'Pause' : 'Play'}
          onClick={() => (isPlaying ? stop() : start())}
        >
          {isPlaying ? <IconPlayerPauseFilled size={13} /> : <IconPlayerPlayFilled size={13} />}
        </button>
        <button
          style={btnStyle}
          title="Step forward"
          onClick={() => { stop(); setActiveDatasetState({ time: Math.min(max, currentIndex + 1) }) }}
        ><IconPlayerTrackNextFilled size={13} /></button>

        <div
          ref={trackRef}
          onPointerDown={onPlayheadTrackPointerDown}
          style={{ flex: 1, position: 'relative', height: 26, cursor: 'pointer' }}
        >
          <div style={{
            position: 'absolute', top: 11, left: 0, right: 0, height: 4,
            background: 'var(--rule)', borderRadius: 2, pointerEvents: 'none',
          }} />
          <input
            type="range"
            min={0}
            max={max}
            step={1}
            value={currentIndex}
            onChange={(e) => { stop(); setActiveDatasetState({ time: parseInt(e.target.value, 10) }) }}
            aria-label="Current timestep"
            aria-valuetext={label}
            style={{
              position: 'absolute', left: 0, right: 0, top: 3, width: '100%',
              margin: 0, accentColor: 'var(--teal)', background: 'transparent', zIndex: 2,
            }}
          />
        </div>

        {stepLabel && (
          <span style={{ fontFamily: 'var(--card-mono)', fontSize: 10, color: 'var(--grey)', whiteSpace: 'nowrap' }}>
            {stepLabel}
          </span>
        )}
        <span style={{
          fontFamily: 'var(--card-mono)', color: 'var(--navy)',
          minWidth: 130, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
        }}>{label}</span>

        {/* Mean toggle — always visible in the top bar. */}
        <button
          onClick={() => {
            const next = !timeMeanMode
            setTimeMeanMode(next)
            if (!next && isMeanDisplayed) clearMean()
          }}
          className={`ze-chip${timeMeanMode ? ' ze-chip--active' : ''}`}
          style={{ marginLeft: 4 }}
          aria-pressed={timeMeanMode}
          title={timeMeanMode ? 'Close mean panel' : 'Open mean panel'}
        >
          Mean
        </button>
      </div>

      {/* ---------- Lane 2: brush + cost + actions ---------- */}
      {timeMeanMode && (
        <div style={{ marginTop: 10 }}>
          <div
            ref={brushTrackRef}
            onPointerDown={brushPointerDown}
            style={{ position: 'relative', height: 22, cursor: 'ew-resize', touchAction: 'none' }}
          >
            {/* Track baseline */}
            <div style={{
              position: 'absolute', top: 9, left: 0, right: 0, height: 4,
              background: 'var(--rule)', borderRadius: 2, pointerEvents: 'none',
            }} />
            {/* Shaded window */}
            <div style={{
              position: 'absolute', top: 9,
              left: `${pctStart}%`,
              width: `${Math.max(0, pctEnd - pctStart)}%`,
              height: 4,
              background: '#CCE7F4',
              borderRadius: 2,
              pointerEvents: 'none',
            }} />
            {/* Handles — rectangular navy so they don't compete with the
                round playhead thumb. */}
            <div
              role="slider"
              aria-label="Mean window start"
              aria-valuemin={0}
              aria-valuemax={max}
              aria-valuenow={brushStart}
              aria-valuetext={startLabel}
              tabIndex={0}
              onKeyDown={(e) => {
                if (!formatTimeIndex) return
                if (e.key === 'ArrowLeft')  setTimeMeanStartDate(formatTimeIndex(Math.max(0, brushStart - 1)))
                if (e.key === 'ArrowRight') setTimeMeanStartDate(formatTimeIndex(Math.min(brushEnd, brushStart + 1)))
              }}
              style={{
                position: 'absolute', top: 3,
                left: `calc(${pctStart}% - 4px)`,
                width: 8, height: 16,
                background: 'var(--navy)', borderRadius: 2,
                zIndex: 3,
              }}
            />
            <div
              role="slider"
              aria-label="Mean window end"
              aria-valuemin={0}
              aria-valuemax={max}
              aria-valuenow={brushEnd}
              aria-valuetext={endLabel}
              tabIndex={0}
              onKeyDown={(e) => {
                if (!formatTimeIndex) return
                if (e.key === 'ArrowLeft')  setTimeMeanEndDate(formatTimeIndex(Math.max(brushStart, brushEnd - 1)))
                if (e.key === 'ArrowRight') setTimeMeanEndDate(formatTimeIndex(Math.min(max, brushEnd + 1)))
              }}
              style={{
                position: 'absolute', top: 3,
                left: `calc(${pctEnd}% - 4px)`,
                width: 8, height: 16,
                background: 'var(--navy)', borderRadius: 2,
                zIndex: 3,
              }}
            />
          </div>

          {/* Cost + actions */}
          <div style={{
            marginTop: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
              fontFamily: 'var(--card-mono)', fontSize: 10, color: 'var(--slate)',
              fontVariantNumeric: 'tabular-nums',
            }}>
              <span>{startLabel} → {endLabel}</span>
              <span style={{ color: 'var(--grey)' }}>·</span>
              <span style={{
                color: windowSteps > HEAVY_MEAN_STEPS ? '#c0392b' : 'var(--navy)',
                fontWeight: 600,
              }}>
                {windowSteps.toLocaleString()} steps
              </span>
              <span style={{ color: 'var(--grey)' }}>of {(max + 1).toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--card-sans)' }}>
              <button
                onClick={() => setEditingDates((v) => !v)}
                className="ze-btn"
                style={{ fontSize: 10 }}
                title="Type exact dates"
              >
                {editingDates ? 'Close dates' : 'Edit dates'}
              </button>
              {isMeanDisplayed && (
                <button
                  onClick={clearMean}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--grey)', fontSize: 10, fontFamily: 'inherit',
                  }}
                >clear</button>
              )}
              <button
                className="ze-btn"
                onClick={requestCompute}
                disabled={!zarrLayer || timeMeanLoading}
              >
                {timeMeanLoading ? 'Computing…' : 'Compute mean'}
              </button>
            </div>
          </div>

          {/* Optional exact-date entry */}
          {editingDates && (
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <input
                type="date"
                value={timeMeanStartDate ?? ''}
                onChange={(e) => setTimeMeanStartDate(e.target.value || null)}
                style={inputStyle}
              />
              <input
                type="date"
                value={timeMeanEndDate ?? ''}
                onChange={(e) => setTimeMeanEndDate(e.target.value || null)}
                style={inputStyle}
              />
            </div>
          )}

          {meanError && (
            <div style={{ fontSize: 10, color: '#c0392b', marginTop: 4 }}>{meanError}</div>
          )}
        </div>
      )}
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 3,
  padding: '2px 4px',
  fontSize: 11,
  fontFamily: 'var(--card-mono)',
  color: 'var(--navy)',
  background: 'var(--paper)',
  outline: 'none',
}

export default TimeSlider
